/**
 * Modal for inviting a user to a vault
 */

import { useState } from "react";
import { useCreateInvitation } from "../../hooks/useInvitations";
import LoadingSpinner from "../Common/LoadingSpinner";

interface AddMemberModalProps {
  vaultId: number;
  isOpen: boolean;
  onClose: () => void;
}

export default function AddMemberModal({
  vaultId,
  isOpen,
  onClose,
}: AddMemberModalProps) {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<"VAULT_CO_ADMIN" | "VAULT_MEMBER">("VAULT_MEMBER");
  const [expiryDays, setExpiryDays] = useState<number>(7);
  const [error, setError] = useState<string | null>(null);
  const [invitationLink, setInvitationLink] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const createInvitationMutation = useCreateInvitation(vaultId);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!email.trim()) {
      setError("Email address is required");
      return;
    }

    if (!email.includes("@")) {
      setError("Please enter a valid email address");
      return;
    }

    try {
      const invitation = await createInvitationMutation.mutateAsync({
        email: email.trim(),
        role,
        expires_in_days: expiryDays,
      });
      
      // Show invitation link
      setInvitationLink(invitation.invitation_link);
      
      // Auto-copy to clipboard (don't show "Copied!" state for auto-copy)
      await navigator.clipboard.writeText(invitation.invitation_link);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create invitation");
    }
  };

  const handleCopyLink = async () => {
    if (invitationLink) {
      await navigator.clipboard.writeText(invitationLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleClose = () => {
    setError(null);
    setEmail("");
    setRole("VAULT_MEMBER");
    setExpiryDays(7);
    setInvitationLink(null);
    setCopied(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" onClick={handleClose} />

      {/* Modal */}
      <div className="relative w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
        {/* Close button */}
        <button
          onClick={handleClose}
          className="absolute right-4 top-4 text-vault-400 hover:text-vault-600"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={1.5}
            stroke="currentColor"
            className="h-6 w-6"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>

        {/* Content */}
        <h2 className="font-serif text-2xl font-semibold text-vault-900 mb-4">
          Invite User to Vault
        </h2>

        {invitationLink ? (
          /* Success State */
          <div className="space-y-4">
            <div className="rounded-md bg-green-50 border border-green-200 p-4">
              <div className="flex items-start gap-3">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={2}
                  stroke="currentColor"
                  className="h-5 w-5 text-green-600 mt-0.5"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M4.5 12.75l6 6 9-13.5"
                  />
                </svg>
                <div className="flex-1">
                  <h3 className="text-sm font-medium text-green-800">
                    Invitation Created!
                  </h3>
                  <p className="mt-1 text-sm text-green-700">
                    Share this link with {email} to invite them to the vault.
                  </p>
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-vault-700 mb-2">
                Invitation Link
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={invitationLink}
                  readOnly
                  className="flex-1 rounded-md border border-vault-300 px-3 py-2 text-sm bg-vault-50 font-mono text-xs"
                />
                <button
                  onClick={handleCopyLink}
                  className="rounded-md bg-vault-600 px-4 py-2 text-sm font-medium text-white hover:bg-vault-700 transition-colors"
                >
                  {copied ? "Copied!" : "Copy"}
                </button>
              </div>
            </div>

            <div className="flex gap-3 pt-2">
              <button
                onClick={handleClose}
                className="flex-1 rounded-md bg-vault-600 px-4 py-2 text-sm font-medium text-white hover:bg-vault-700 transition-colors"
              >
                Done
              </button>
            </div>
          </div>
        ) : (
          /* Form State */
          <>
            <p className="text-sm text-vault-600 mb-6">
              Create an invitation link to invite a user to join this vault. You can share the link via email, messaging, or any other method.
            </p>

            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-700">
                  {error}
                </div>
              )}

              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-medium text-vault-700 mb-1"
                >
                  Email Address
                </label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="user@example.com"
                  className="w-full rounded-md border border-vault-300 px-3 py-2 text-sm focus:border-vault-500 focus:outline-none focus:ring-1 focus:ring-vault-500"
                  required
                />
              </div>

              <div>
                <label
                  htmlFor="role"
                  className="block text-sm font-medium text-vault-700 mb-1"
                >
                  Role
                </label>
                <select
                  id="role"
                  value={role}
                  onChange={(e) =>
                    setRole(e.target.value as "VAULT_CO_ADMIN" | "VAULT_MEMBER")
                  }
                  className="w-full rounded-md border border-vault-300 px-3 py-2 text-sm focus:border-vault-500 focus:outline-none focus:ring-1 focus:ring-vault-500"
                >
                  <option value="VAULT_MEMBER">Member (View & Contribute)</option>
                  <option value="VAULT_CO_ADMIN">Co-Admin (Full Management)</option>
                </select>
                <p className="mt-1 text-xs text-vault-500">
                  Members can view and contribute. Co-Admins can manage members and settings.
                </p>
              </div>

              <div>
                <label
                  htmlFor="expiry"
                  className="block text-sm font-medium text-vault-700 mb-1"
                >
                  Link Expires In
                </label>
                <select
                  id="expiry"
                  value={expiryDays}
                  onChange={(e) => setExpiryDays(Number(e.target.value))}
                  className="w-full rounded-md border border-vault-300 px-3 py-2 text-sm focus:border-vault-500 focus:outline-none focus:ring-1 focus:ring-vault-500"
                >
                  <option value={7}>7 days</option>
                  <option value={14}>14 days</option>
                  <option value={30}>30 days</option>
                  <option value={90}>90 days</option>
                </select>
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={handleClose}
                  className="flex-1 rounded-md border border-vault-300 px-4 py-2 text-sm font-medium text-vault-700 hover:bg-vault-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={createInvitationMutation.isPending}
                  className="flex-1 rounded-md bg-vault-600 px-4 py-2 text-sm font-medium text-white hover:bg-vault-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {createInvitationMutation.isPending ? (
                    <>
                      <LoadingSpinner size="sm" />
                      Creating...
                    </>
                  ) : (
                    "Create Invitation"
                  )}
                </button>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
